import { Alert, Button } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import * as H from 'history';
import React, { Suspense, useEffect, useRef } from 'react';
import AclRouter from 'react-acl-router';
import { useSelector } from 'react-redux';
import {
  Link,
  match,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';

import { RootState } from 'src/app/store';

import { useGetDisplayNpsFormQuery } from '../api/apigQuery';
import { Logger } from '../common/Logger';
import { useMakePHPRequest } from '../common/useMakePHPRequest';
import { IBusinessUser, IUserAtom, Role } from '../recoil/atoms/user';
import { methodNameEnum } from '../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../services/rest/makeAPIRequest';

import CallTranscriptionRedirect from './components/CallTranscriptions/CallTranscriptionRedirect';
import Meta from './components/Meta';
import { NewTrialModal } from './components/modals/NewTrialModal';
import { NPSModal } from './components/modals/NPSModal';
import UpdateCardModal from './components/modals/UpdateCardModal';
import { useSnackbarWithStyles } from './components/notifications/SnackbarProviderWithStyles';
import { showUpdateCard } from './components/stripe/showUpdateCard';
import { ErrorPage1 } from './modules/ErrorsExamples/ErrorPage1';
import { QueuesPage } from './modules/Manage/pages/QueuesPage';
import { GenericSkeleton } from './modules/Skeleton/GenericSkeleton';
import { QueuesSkeleton } from './modules/Skeleton/QueuesSkeleton';
import { TabsSkeleton } from './modules/Skeleton/TabsSkeleton';
import { UnauthorizedPage } from './modules/Unauthorized/UnauthorizedPage';
import { AIOLeadFlow } from './pages/aio/AIOLeadFlow';
import AuditMetaData from './pages/AuditMetaData';
import BIDSearch from './pages/BIDSearch';
import { BillingTabs } from './pages/BillingTabs';
import { CallBoard } from './pages/CallBoard';
import { CallReportingTabs } from './pages/CallReportingTabs';
import { CompletedActions } from './pages/CompletedActions';
import { ContactsPage } from './pages/Contacts';
import { DashboardPage } from './pages/DashboardPage';
import { HistoryTabs } from './pages/HistoryTabs';
import { InflightCadences } from './pages/InflightCadences';
import { Webhooks } from './pages/manage/webooks/Webhooks';
import Manage10DLC from './pages/Manage10DLC';
import ManageAccount from './pages/ManageAccount';
import { ManageAgents } from './pages/ManageAgents';
import { ManageCadences2 } from './pages/ManageCadences2';
import { ManageConferences } from './pages/ManageConferences';
import { ManageContacts } from './pages/ManageContacts';
import { ManageDispositions } from './pages/ManageDispositions';
import { ManageIvrs } from './pages/ManageIvrs';
import { ManageLeaderboards } from './pages/ManageLeaderboards';
import { ManagePowerlists } from './pages/ManagePowerLists';
import { ManageTeams } from './pages/ManageTeams';
import { NumberTabs } from './pages/NumberTabs';
import OnboardingWalkthroughTabs from './pages/OnboardingWalkthroughTabs';
import { PowerlistContacts } from './pages/PowerlistContacts';
import PowerlistDispositionRules from './pages/PowerlistDispositionRules';
import { RingGroups } from './pages/RingGroups';
import { ViewAgentReportingTabs } from './pages/ViewAgentReportingTabs';
import { ViewAgentTabs } from './pages/ViewAgentTabs';
import { ViewTeam } from './pages/ViewTeam';
import ApiUsage from './subpages/ApiUsage';
import AuditTrail from './subpages/AuditTrail';
import { JiraReports } from './subpages/JiraReports';
import { LeaderboardSlides } from './subpages/LeaderboardSlides';
import TermsGate from './subpages/TermsGate';

const useStyles = makeStyles((theme: Theme) => ({
  banner: {
    '&:has(div)': {
      marginBottom: '2rem',
    },
    backgroundColor: theme.palette.background.paper,
    '& .MuiCard-root': {
      display: 'flex',
      flex: '1 1 0',
      alignItems: 'center',
      margin: theme.spacing(1),
    },
    '& .MuiBanner-cardContent': {
      padding: theme.spacing(1),
      flex: '1 1 0',
    },
  },
  alert: {
    '& .MuiAlert-icon': {
      alignItems: 'center',
    },
    '& .MuiAlert-message': {
      display: 'flex',
      flex: '1 1 0',
      alignItems: 'center',
    },
    '& p': {
      margin: 0,
      flexGrow: 1,
    },
  },
  add: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    marginLeft: theme.spacing(1), // space between fields
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  subscriptionMetricsSpacing: {
    '&:has(div)': {
      marginBottom: '1rem',
    },
  },
}));

const ToManageLanding = () => (
  <Redirect from={'/manage'} to={'/manage/agents'} />
);

export const UpdateCardModalWrapper = ({
  history,
  match,
}: {
  history: H.History;
  match: match;
}) => (
  <UpdateCardModal
    onClose={() => {
      // go to previous page if one exists else go to root
      if (history.location.state) {
        history.goBack();
      } else {
        history.push('/');
      }
    }}
  />
);

const ViewAgentTabsWrapper = ({
  history,
}: // match,
{
  history: H.History;
  match: match;
}) => <ViewAgentTabs history={history} basePath="/my-profile/:id" />;
const ViewAgentReportingTabsWrapper = ({
  history,
}: // match,
{
  history: H.History;
  match: match;
}) => <ViewAgentReportingTabs history={history} basePath="/my-profile/:id" />;

export interface IAuthorizedRoutes {
  path: string;
  permissions: Role[];
  component: React.ReactNode;
  redirect: string;
  unauthorized?: React.ReactNode;
}

export const authorizedRoutes = (
  permissions: IPermissions
): IAuthorizedRoutes[] => [
  {
    path: '/billing',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: BillingTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/call-board',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: CallBoard,
    redirect: '/unauthorized',
  },
  {
    path: '/call-reporting/audit-trail',
    permissions: [Role.SUPERADMIN],
    component: AuditTrail,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/internal/jira-issues',
    permissions: [Role.SUPERADMIN],
    component: JiraReports,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/internal/api-usage',
    permissions: [Role.SUPERADMIN],
    component: ApiUsage,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/internal/bid-search',
    permissions: [Role.SUPERADMIN],
    component: BIDSearch,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/internal/audit-meta-data',
    permissions: [Role.SUPERADMIN],
    // permissions:
    //   permissions?.canManageAuditMetaData === true
    //     ? []
    //     : ([undefined] as any[]),
    component: AuditMetaData,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/call-reporting',
    permissions: [
      Role.SUPERADMIN,
      Role.ADMIN,
      Role.MANAGER,
      ...(permissions.canViewCallReporting ? [Role.USER] : []),
    ],
    component: CallReportingTabs,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/contacts',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER],
    component: ContactsPage,
    redirect: '/unauthorized',
  },
  {
    path: '/dashboard',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER],
    component: DashboardPage,
    redirect: '/unauthorized',
  },
  {
    path: '/history',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: HistoryTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/account',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageAccount,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  // more specifice routes need to come first
  {
    path: '/manage/agents/:id/edit',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/:id/devices-and-routing',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/:id/numbers',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/:id/powerlists',
    permissions:
      permissions.allowAgentPowerlist && permissions.canAccessPowerlistProduct
        ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
        : [],
    component: ViewAgentTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/:id/sms-template-help',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/:id/vm-drop',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/:id/agent-snapshot',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentReportingTabs,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/manage/agents/:id/call-history',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentReportingTabs,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/manage/agents/:id/agent-reporting',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentReportingTabs,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/manage/agents/:id/sms-history',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentReportingTabs,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/manage/agents',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageAgents,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/products',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageAgents,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/agents/:id',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewAgentTabs,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/manage/10dlc',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: Manage10DLC,
    redirect: '/unauthorized',
    unauthorized: UnauthorizedPage,
  },
  {
    path: '/manage/cadences/inflight-cadences',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: InflightCadences,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/cadences/completed-actions',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: CompletedActions,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/cadences',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageCadences2,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/conferences',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageConferences,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/dispositions',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageDispositions,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/ivrs',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageIvrs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/numbers',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: NumberTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/queues',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: QueuesPage,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/ring-groups',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: RingGroups,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/leaderboards/:id',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: LeaderboardSlides,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/leaderboards',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageLeaderboards,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/teams',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManageTeams,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/powerlists/:id/view-contacts',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: PowerlistContacts,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/powerlists/view-disposition-rules',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: PowerlistDispositionRules,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/powerlists',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ManagePowerlists,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/webhooks',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: Webhooks,
    redirect: '/unauthorized',
  },
  {
    path: '/manage/contacts',
    permissions: [Role.SUPERADMIN],
    component: ManageContacts,
    redirect: '/unauthorized',
  },
  {
    path: '/manage',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ToManageLanding,
    redirect: '/unauthorized',
  },
  {
    path: '/my-profile/:id/edit',
    permissions:
      permissions.allowAgentPowerlist && permissions.canAccessPowerlistProduct
        ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
        : [],
    component: ViewAgentTabsWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/my-profile/:id/devices-and-routing',
    permissions:
      permissions.allowAgentPowerlist && permissions.canAccessPowerlistProduct
        ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
        : [],
    component: ViewAgentTabsWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/my-profile/:id/numbers',
    permissions:
      permissions.allowAgentPowerlist && permissions.canAccessPowerlistProduct
        ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
        : [],
    component: ViewAgentTabsWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/my-profile/:id/powerlists',
    permissions:
      permissions.allowAgentPowerlist && permissions.canAccessPowerlistProduct
        ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
        : [],
    component: ViewAgentTabsWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/my-profile/:id/sms-template-help',
    permissions:
      permissions.allowAgentPowerlist && permissions.canAccessPowerlistProduct
        ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
        : [],
    component: ViewAgentTabsWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/my-profile/:id/vm-drop',
    permissions:
      permissions.allowAgentPowerlist && permissions.canAccessPowerlistProduct
        ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
        : [],
    component: ViewAgentTabsWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/my-profile/:id',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER],
    component: ViewAgentReportingTabsWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/update-card',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: UpdateCardModalWrapper,
    redirect: '/unauthorized',
  },
  {
    path: '/viewTeam/:id',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: ViewTeam,
    redirect: '/unauthorized',
  },
  {
    path: '/onboarding-walkthrough',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: OnboardingWalkthroughTabs,
    redirect: '/unauthorized',
  },
  {
    path: '/transcription/:callXd',
    permissions: permissions.canAccessCallTranscriptionProduct
      ? [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.USER]
      : [],
    component: CallTranscriptionRedirect,
    redirect: '/unauthorized',
  },
  {
    path: '/lead-flow',
    permissions: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER],
    component: AIOLeadFlow,
    redirect: '/unauthorized',
  },
];

interface IBasePage {
  basePath?: string;
  userAtom: IUserAtom;
}

export default function BasePage({ basePath = '/', userAtom }: IBasePage) {
  const classes = useStyles();
  const location = useLocation();
  const {
    apikey,
    businessid,
    user: { role, userid, callerid, calleridName },
  } = userAtom;
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessDataLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );

  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const permissions = permissionsLoadable.contents as IPermissions;

  const [_, myBusinessUsersLoadable] = makeApiRequestCached<
    IBusinessUser | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.find?.(
        (user) => user.userid == userid
      ) ?? cached.current,
    undefined,
    [userid]
  );

  const businessUser = React.useMemo(() => {
    if (myBusinessUsersLoadable?.contents?.response?.data?.result) {
      return myBusinessUsersLoadable.contents.response.data.result.find?.(
        (bu) => bu.userid === userid
      );
    }
    return null;
  }, [myBusinessUsersLoadable, userid]);

  const onboardingEnabled = React.useMemo(
    () =>
      userAtom?.onboardingWalkthrough?.displayOnboarding &&
      process.env.VITE_APP_ONBOARDING === '1' &&
      permissions.displayOW,
    [userAtom]
  );

  const powercallInstalledRef = React.useRef<any>(null);

  // const foundPowercallInstalledDiv = document.getElementById(
  //   'powercall-installed'
  // );

  const [powercallInstalled, setPowercallInstalled] = React.useState(true);
  const [showNewTrialModal, setShowNewTrialModal] = React.useState(false);
  const modalShownRef = useRef(false);
  const { data: displayNpsForm, refetch } = useGetDisplayNpsFormQuery(
    {
      userSettings: {
        userid: userid,
        businessId: businessid,
        apikey: apikey,
      },
      // locationName: undefined,
    },
    {
      skip: !businessid,
      refetchOnMountOrArgChange: true,
    }
  );
  const [showNPSModal, setShowNPSModal] = React.useState(false);
  useEffect(() => {
    setShowNPSModal(Boolean(displayNpsForm));
  }, [displayNpsForm]);

  const roleIsManagerPlus = React.useMemo(() => {
    return role === 'superadmin' || role === 'admin' || role === 'manager';
  }, [role]);

  function checkPowercallInstalled() {
    try {
      const powercallInstalledDiv = document.getElementById(
        'powercall-installed'
      );
      if (!!powercallInstalledDiv || businessUser?.version) {
        powercallInstalledDiv?.remove?.();
        setPowercallInstalled(true);
      } else {
        setPowercallInstalled(false);
      }
    } catch (e) {
      //
    }
  }

  useEffect(() => {
    // setTimeout(() => {
    if (businessUser) {
      try {
        const powercallInstalledDiv = document.getElementById(
          'powercall-installed'
        );
        if (powercallInstalledDiv || businessUser?.version) {
          setPowercallInstalled(true);
        } else {
          setPowercallInstalled(false);
        }
      } catch (e) {
        //
      }
      if (!businessUser?.version) {
        if (powercallInstalledRef.current) {
          clearInterval(powercallInstalledRef.current);
        }
        powercallInstalledRef.current = setInterval(() => {
          checkPowercallInstalled();
        }, 5000);
      } else {
        if (powercallInstalledRef.current) {
          clearInterval(powercallInstalledRef.current);
        }
        powercallInstalledRef.current = null;
      }
    }
    // }, 1000);

    return () => {
      if (powercallInstalledRef.current) {
        clearInterval(powercallInstalledRef.current);
        powercallInstalledRef.current = null;
      }
    };
  }, [businessUser]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const puppeteerSkipWelcome = urlParams.get('pup-skip-welcome') === '1';
    const shouldShowOnboardingModal =
      onboardingEnabled && !puppeteerSkipWelcome;
    const shouldShowPowerCallModal =
      !powercallInstalled &&
      businessUser &&
      !businessUser.version &&
      !puppeteerSkipWelcome;

    const shouldShowModal =
      (shouldShowOnboardingModal || shouldShowPowerCallModal) &&
      !modalShownRef.current;

    if (shouldShowModal) {
      setShowNewTrialModal(true);
      modalShownRef.current = true;
    }
  }, [location.search, onboardingEnabled, powercallInstalled, businessUser]);

  // log path changes
  useEffect(() => {
    if (businessDataLoadable?.state === 'hasValue') {
      Logger.debug(
        {
          pathname: window.location.pathname,
          businessid,
          apikey: apikey,
          role,
          userid,
          callerid,
          calleridName,
        },
        'BasePage'
      );
    }
  }, [businessDataLoadable, basePath, userAtom, window.location.pathname]);
  const largeTable = useSelector((state: RootState) => {
    return state.largeTable.displayLargeTableMsg;
  });
  const phpRequest = useMakePHPRequest({
    userAtom,
  });
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const insufficientMinutesReserveBalance = React.useMemo(() => {
    return (
      businessDataLoadable.state === 'hasValue' &&
      businessDataLoadable.contents?.response?.data?.result?.[0]
        ?.reserveBalance <= 0
    );
  }, [businessDataLoadable]);

  return (
    <Suspense
      fallback={
        /*
         * Choose specific skeletons based on routes
         */
        <Switch>
          <Route path="/manage/queues" component={QueuesSkeleton} />
          <Route path="/call-reporting" component={TabsSkeleton} />
          <Route path="/history" component={TabsSkeleton} />
          <GenericSkeleton />
        </Switch>
      }
    >
      <div
        className={
          window.location.pathname === '/billing/subscription'
            ? clsx(classes.banner, classes.subscriptionMetricsSpacing)
            : classes.banner
        }
      >
        {permissions.accountReadOnly && (
          <Alert severity="error" className={classes.alert}>
            <p>
              Trial has Expired. Did you like our service? Give us a call and
              let's make a deal 424-800-3330 .
            </p>
            <Button
              className={classes.add}
              onClick={showUpdateCard(
                enqueueSnackbarWithStyles,
                phpRequest,
                JSON.stringify(userid)
              )}
            >
              Add Card
            </Button>
          </Alert>
        )}
        {permissions.cardDeclined && (
          <Alert severity="error">
            Credit Card Declined. Our last attempt to charge your card was
            declined. Please update your card details to prevent an outage to
            your service.
          </Alert>
        )}
        {permissions.starterLimitMessage &&
          (role === Role.SUPERADMIN ||
            role === Role.ADMIN ||
            role === Role.MANAGER) && (
            <Alert severity="warning">
              Please{' '}
              <Link to="/manage/10dlc">
                <strong>register</strong>
              </Link>{' '}
              for 10DLC ASAP if you send SMS messages to US numbers. Failure to
              register will result in a block for all outgoing SMS from your
              account after December 1st, 2024. 10DLC registration is now an
              industry-wide requirement to send outbound SMS messages to US long
              code numbers. This is to reduce unsolicited SMS messages towards
              US consumers who have not directly opted into SMS communication
              from your business.{' '}
              <a
                href="https://support.kixie.com/hc/en-us/articles/16188947660571-10DLC-Registration-Recommendations"
                target="_blank"
                rel="noopener noreferrer"
              >
                See here
              </a>{' '}
              for a complete guide on how to register successfully.
            </Alert>
          )}
        {largeTable && (
          <Alert severity="info">
            This table shows only up to ten thousand rows when viewed in the
            Kixie Dashboard. Use the CSV export to view all rows.
          </Alert>
        )}
        {permissions.starterLimitMessage && role === Role.USER && (
          <Alert severity="warning">
            Please{' '}
            <Link to="/manage/10dlc">
              <strong>register</strong>
            </Link>{' '}
            for 10DLC ASAP if you send SMS messages to US numbers. Failure to
            register will result in a block for all outgoing SMS from your
            account after December 1st, 2024. 10DLC registration is now an
            industry-wide requirement to send outbound SMS messages to US long
            code numbers. This is to reduce unsolicited SMS messages towards US
            consumers who have not directly opted into SMS communication from
            your business.{' '}
            <a
              href="https://support.kixie.com/hc/en-us/articles/16188947660571-10DLC-Registration-Recommendations"
              target="_blank"
              rel="noopener noreferrer"
            >
              See here
            </a>{' '}
            for a complete guide on how to register successfully.
          </Alert>
        )}
        {insufficientMinutesReserveBalance &&
          (role === Role.SUPERADMIN ||
            role === Role.ADMIN ||
            role === Role.MANAGER) &&
          (window.location.pathname !== '/billing/subscription' ? (
            <Alert severity="error">
              Your Minutes Reserve balance has reached $0. This will interrupt
              calling and SMS for agents on your account. Please take immediate
              action to add additional funds to your Minutes Reserve by visiting
              the Subscription page under the Billing section of the Kixie
              Dashboard
              <Link to="/billing/subscription">
                <strong> here</strong>
              </Link>
              . This alert will remain until the balance is replenished.
            </Alert>
          ) : (
            <Alert severity="error">
              Your Minutes Reserve balance has reached $0. This will interrupt
              calling and SMS for agents on your account. Please take immediate
              action to add additional funds to your Minutes Reserve. This alert
              will remain until the balance is replenished.
            </Alert>
          ))}
      </div>
      <Meta />
      <Switch>
        {/*
         * Redierct to call-reporting at start
         * NOTE: routes are resolved from top to bottom therefore you
         * must put the most specific routes at before generic routes
         */}
        <Redirect
          exact
          from={'/index.html'}
          to={{
            pathname:
              role === Role.USER
                ? `/my-profile/${userid}`
                : onboardingEnabled && (Role.ADMIN || Role.MANAGER)
                ? '/onboarding-walkthrough'
                : '/call-reporting',
            search: location.search,
          }}
        />
        <Redirect
          exact
          from={basePath}
          to={{
            pathname:
              role === Role.USER
                ? `/my-profile/${userid}`
                : onboardingEnabled && (Role.ADMIN || Role.MANAGER)
                ? '/onboarding-walkthrough'
                : '/call-reporting',
            search: location.search,
          }}
        />
        {!sessionStorage.getItem('redirect_url') && (
          <Redirect
            exact
            from="/login"
            to={{
              pathname:
                role === Role.USER
                  ? `/my-profile/${userid}`
                  : '/call-reporting',
              search: location.search,
            }}
          />
        )}
        <Redirect
          from="/change-password"
          to={{
            pathname:
              role === Role.USER
                ? `/my-profile/${userid}/agent-snapshot`
                : '/call-reporting',
            search: location.search,
          }}
        />

        {/* Manage section */}
        {/* <Route
          path="/manage/ring-groups/:id"
          component={RingGroupSettings}
        /> */}

        {/* Skeleton section */}
        <Route path="/skeleton/queues" component={QueuesSkeleton} />
        <Route path="/skeleton/tabs" component={TabsSkeleton} />
        <Route path="/skeleton" component={GenericSkeleton} />

        {/* Unauthorized Page */}
        <Route path="/unauthorized" component={UnauthorizedPage} />

        <AclRouter
          authorities={role}
          authorizedRoutes={authorizedRoutes(permissions)}
          notFound={() => <ErrorPage1 />}
        />

        {/* Default to error pages */}
        <Redirect to="error/error-v1" />
      </Switch>
      <NewTrialModal
        show={showNewTrialModal && roleIsManagerPlus}
        handleClose={() => setShowNewTrialModal(false)}
        requirePowerCall={!powercallInstalled}
        requireOnboarding={onboardingEnabled}
      />
      <NPSModal
        show={showNPSModal}
        handleClose={() => {
          setShowNPSModal(false);
          refetch();
        }}
      />
      <TermsGate />
    </Suspense>
  );
}
